import type { AuditEntry } from '@voll-intelligence/domain';

import { t } from '@web/i18n';

export type HistoryIcon =
  | 'archive'
  | 'user-plus'
  | 'arrow-down-right'
  | 'arrow-u-left'
  | 'check-circle'
  | 'rows-plus'
  | 'arrows-left-right'
  | 'play-circle'
  | 'prohibit'
  | 'arrow-counter-clockwise'
  | 'warning'
  | 'note-pencil'
  | 'circle';

export type HistoryTone = 'green' | 'amber' | 'red' | 'gray' | 'ai';

export interface HistoryRowView {
  icon: HistoryIcon;
  tone: HistoryTone;
  title: string;
  meta: string[];
}

/** Walk a nested key path through an untyped changeset; `undefined` on any miss. */
function field(changeset: Record<string, unknown>, ...path: string[]): unknown {
  let cur: unknown = changeset;
  for (const key of path) {
    if (typeof cur !== 'object' || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/** Coerce to a non-empty string, else `undefined`. */
function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

const compact = (parts: Array<string | undefined>): string[] =>
  parts.filter((p): p is string => Boolean(p));

/** Known triage skip reasons → human i18n label. */
const TRIAGE_REASON_LABEL: Record<string, Parameters<typeof t>[0]> = {
  no_content: 'board.drawer.history.triageReason.noContent',
  no_match: 'board.drawer.history.triageReason.noMatch',
  low_confidence: 'board.drawer.history.triageReason.lowConfidence',
  unknown_code: 'board.drawer.history.triageReason.unknownCode',
};

const SIMPLE_ACTIONS: Partial<
  Record<
    AuditEntry['action'],
    { icon: HistoryIcon; tone: HistoryTone; title: Parameters<typeof t>[0] }
  >
> = {
  'card.claimed': {
    icon: 'arrow-down-right',
    tone: 'gray',
    title: 'board.drawer.history.action.cardClaimed',
  },
  'card.closed': {
    icon: 'check-circle',
    tone: 'green',
    title: 'board.drawer.history.action.cardClosed',
  },
  'card.reopened': {
    icon: 'arrow-u-left',
    tone: 'gray',
    title: 'board.drawer.history.action.cardReopened',
  },
  'card.meta.updated': {
    icon: 'note-pencil',
    tone: 'gray',
    title: 'board.drawer.history.action.cardMetaUpdated',
  },
  'card.assigned': {
    icon: 'user-plus',
    tone: 'gray',
    title: 'board.drawer.history.action.cardAssigned',
  },
  'card.transferred': {
    icon: 'arrows-left-right',
    tone: 'gray',
    title: 'board.drawer.history.action.cardTransferred',
  },
  'card.reassigned': {
    icon: 'arrows-left-right',
    tone: 'gray',
    title: 'board.drawer.history.action.cardReassigned',
  },
  'card.returned-to-triage': {
    icon: 'arrow-u-left',
    tone: 'gray',
    title: 'board.drawer.history.action.cardReturnedToTriage',
  },
  'card.returned-to-agent': {
    icon: 'arrow-u-left',
    tone: 'gray',
    title: 'board.drawer.history.action.cardReturnedToAgent',
  },
  'task.started': {
    icon: 'play-circle',
    tone: 'gray',
    title: 'board.drawer.history.action.taskStarted',
  },
  'task.completed': {
    icon: 'check-circle',
    tone: 'green',
    title: 'board.drawer.history.action.taskCompleted',
  },
  'task.unblocked': {
    icon: 'arrow-counter-clockwise',
    tone: 'gray',
    title: 'board.drawer.history.action.taskUnblocked',
  },
};

/**
 * Map an audit entry to its History row view model. Best-effort: reads
 * only fields the `changeset` already carries, omitting anything absent
 * rather than fabricating it. Unknown actions fall back to the raw
 * action string + the actor kind.
 */
export function presentAuditEntry(entry: AuditEntry, locale = 'pt-BR'): HistoryRowView {
  const cs = entry.changeset;
  const simple = SIMPLE_ACTIONS[entry.action];
  if (simple) return { icon: simple.icon, tone: simple.tone, title: t(simple.title), meta: [] };
  switch (entry.action) {
    case 'card.created':
      return {
        icon: 'archive',
        tone: 'gray',
        title: t('board.drawer.history.action.cardCreated'),
        meta: compact([
          str(field(cs, 'card', 'originChannel')),
          str(field(cs, 'card', 'customer', 'name')),
          str(field(cs, 'card', 'company', 'name')),
        ]),
      };
    case 'card.triaged':
      return {
        icon: 'user-plus',
        tone: 'gray',
        title: t('board.drawer.history.action.cardTriaged'),
        meta: compact([str(field(cs, 'squadCode'))]),
      };
    case 'card.triage_skipped': {
      const reasonRaw = str(field(cs, 'reason'));
      const reasonKey = reasonRaw ? TRIAGE_REASON_LABEL[reasonRaw] : undefined;
      const reasonText = reasonKey ? t(reasonKey) : reasonRaw;
      const confidence = field(cs, 'confidence');
      const meta =
        typeof confidence === 'number' && reasonText
          ? [
              t('board.drawer.history.triageSkipDetail', {
                reason: reasonText,
                confidence: confidence.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              }),
            ]
          : compact([reasonText]);
      return {
        icon: 'prohibit',
        tone: 'amber',
        title: t('board.drawer.history.action.cardTriageSkipped'),
        meta,
      };
    }
    case 'task.created':
      return {
        icon: 'rows-plus',
        tone: 'gray',
        title:
          str(field(cs, 'task', 'description')) ?? t('board.drawer.history.action.taskCreated'),
        meta: compact([str(field(cs, 'task', 'priority'))]),
      };
    case 'task.claim':
      return {
        icon: 'arrows-left-right',
        tone: 'ai',
        title: t('board.drawer.history.action.taskClaim'),
        meta: compact([str(field(cs, 'reason')), str(field(cs, 'note'))]),
      };
    case 'task.blocked':
      return {
        icon: 'prohibit',
        tone: 'amber',
        title: t('board.drawer.history.action.taskBlocked'),
        meta: compact([str(field(cs, 'blockReason'))]),
      };
    case 'task.failed':
      return {
        icon: 'warning',
        tone: 'red',
        title: t('board.drawer.history.action.taskFailed'),
        meta: compact([str(field(cs, 'errorCode'))]),
      };
    default:
      return { icon: 'circle', tone: 'gray', title: entry.action, meta: [entry.actor.kind] };
  }
}
